import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { api } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { ProductStatus } from '@yaatou/types';

export interface ProductMedia {
  id: string;
  type: 'IMAGE' | 'VIDEO';
  url: string;
  publicId: string;
  alt?: string;
  order: number;
}

export interface ProductVariant {
  id: string;
  name: string;
  sku: string;
  price: number | null;
  stock: number;
  attributes: Record<string, string>;
}

export interface Product {
  id: string;
  reference: string;
  name: string;
  description: string;
  basePrice: number;
  wholesalePrice: number | null;
  sourcedFrom: string | null;
  categoryId: string;
  category?: { id: string; name: string; slug: string };
  status: ProductStatus;
  isFeatured: boolean;
  isFavorite?: boolean;
  minOrderQuantity: number;
  media: ProductMedia[];
  variants: ProductVariant[];
  metadata: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  icon?: string;
  children?: Category[];
}

interface ProduitContextValue {
  products: Product[];
  loading: boolean;
  error: string | null;
  fetchProducts: (params?: { categoryId?: string; search?: string; status?: ProductStatus; cursor?: string; limit?: number; admin?: boolean; isFeatured?: boolean; minOrderQuantity?: number }) => Promise<Product[]>;
  fetchProduct: (id: string) => Promise<Product | null>;
  createProduct: (data: {
    name: string;
    description: string;
    basePrice: number;
    categoryId: string;
    isFeatured?: boolean;
    minOrderQuantity?: number;
    wholesalePrice?: number;
    sourcedFrom?: string;
    media: ProductMedia[];
    variants?: Array<{ name: string; sku: string; price?: number; stock: number; attributes: Record<string, string> }>;
    metadata?: Record<string, any>;
  }) => Promise<Product | null>;
  updateProduct: (id: string, data: Partial<Pick<Product, 'name' | 'description' | 'basePrice' | 'categoryId' | 'status' | 'isFeatured' | 'minOrderQuantity' | 'wholesalePrice' | 'sourcedFrom' | 'metadata'>>) => Promise<Product | null>;
  publishProduct: (id: string, status: Product['status']) => Promise<Product | null>;
  deleteProduct: (id: string) => Promise<boolean>;
  addMedia: (productId: string, media: Omit<ProductMedia, 'id' | 'order'>) => Promise<ProductMedia | null>;
  removeMedia: (productId: string, mediaId: string) => Promise<boolean>;
  reorderMedia: (productId: string, media: ProductMedia[]) => Promise<boolean>;
  clearError: () => void;
}

const ProduitContext = createContext<ProduitContextValue | null>(null);

export function useProduit(): ProduitContextValue {
  const ctx = useContext(ProduitContext);
  if (!ctx) throw new Error('useProduit must be used within ProduitProvider');
  return ctx;
}

export function ProduitProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const handleError = useCallback((err: unknown, fallback: string) => {
    const message = err instanceof Error ? err.message : fallback;
    setError(message);
    return null;
  }, []);

  const fetchProducts = useCallback(async (params?: {
    categoryId?: string;
    search?: string;
    status?: ProductStatus;
    cursor?: string;
    limit?: number;
    admin?: boolean;
    isFeatured?: boolean;
    minOrderQuantity?: number;
  }): Promise<Product[]> => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (params?.categoryId) qs.set('categoryId', params.categoryId);
      if (params?.search) qs.set('search', params.search);
      if (params?.status) qs.set('status', params.status);
      if (params?.cursor) qs.set('cursor', params.cursor);
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.isFeatured) qs.set('isFeatured', 'true');
      if (params?.minOrderQuantity) qs.set('minOrderQuantity', String(params.minOrderQuantity));

      const endpoint = params?.admin ? '/products/admin-list' : '/products';
      const data = await api.get<Product[]>(`${endpoint}${qs.toString() ? `?${qs.toString()}` : ''}`);
      const list = Array.isArray(data) ? data : [];
      setProducts(list);
      return list;
    } catch (err) {
      handleError(err, 'Impossible de charger les produits.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const fetchProduct = useCallback(async (id: string): Promise<Product | null> => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<Product>(`/products/${id}`);
      return data;
    } catch (err) {
      return handleError(err, 'Impossible de charger le produit.');
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const createProduct = useCallback(async (data: {
    name: string;
    description: string;
    basePrice: number;
    categoryId: string;
    isFeatured?: boolean;
    minOrderQuantity?: number;
    wholesalePrice?: number;
    sourcedFrom?: string;
    media: ProductMedia[];
    variants?: Array<{ name: string; sku: string; price?: number; stock: number; attributes: Record<string, string> }>;
    metadata?: Record<string, any>;
  }): Promise<Product | null> => {
    setLoading(true);
    setError(null);
    try {
      const payload: Record<string, any> = {
        name: data.name.trim(),
        description: data.description.trim(),
        basePrice: data.basePrice,
        categoryId: data.categoryId,
        isFeatured: data.isFeatured || false,
        minOrderQuantity: data.minOrderQuantity || 1,
        metadata: data.metadata || {},
      };
      if (data.wholesalePrice !== undefined) {
        payload.wholesalePrice = data.wholesalePrice;
      }
      if (data.sourcedFrom !== undefined) {
        payload.sourcedFrom = data.sourcedFrom;
      }

      if (data.variants && data.variants.length > 0) {
        payload.variants = data.variants;
      }

      const product = await api.post<Product>('/products', payload);

      if (data.media.length > 0 && product?.id) {
        await Promise.allSettled(
          data.media.map((m) =>
            api.post<ProductMedia>(`/products/${product.id}/media`, {
              type: m.type,
              url: m.url,
              publicId: m.publicId,
              alt: m.alt || data.name,
            }).catch(() => null),
          ),
        );
      }

      const created = await fetchProduct(product.id).then((r) => r).catch(() => product);
      setProducts((prev) => [created, ...prev]);
      return created;
    } catch (err) {
      return handleError(err, 'Erreur lors de la création du produit.');
    } finally {
      setLoading(false);
    }
  }, [fetchProduct, handleError]);

  const updateProduct = useCallback(async (id: string, data: Partial<Pick<Product, 'name' | 'description' | 'basePrice' | 'categoryId' | 'status' | 'isFeatured' | 'minOrderQuantity' | 'wholesalePrice' | 'sourcedFrom' | 'metadata'>>): Promise<Product | null> => {
    setLoading(true);
    setError(null);
    try {
      const payload: Record<string, any> = {};
      if (data.name !== undefined) payload.name = data.name.trim();
      if (data.description !== undefined) payload.description = data.description.trim();
      if (data.basePrice !== undefined) payload.basePrice = data.basePrice;
      if (data.categoryId !== undefined) payload.categoryId = data.categoryId;
      if (data.status !== undefined) payload.status = data.status;
      if (data.isFeatured !== undefined) payload.isFeatured = data.isFeatured;
      if (data.minOrderQuantity !== undefined) payload.minOrderQuantity = data.minOrderQuantity;
      if (data.wholesalePrice !== undefined) payload.wholesalePrice = data.wholesalePrice;
      if (data.sourcedFrom !== undefined) payload.sourcedFrom = data.sourcedFrom;
      if (data.metadata !== undefined) payload.metadata = data.metadata;

      const updated = await api.patch<Product>(`/products/${id}`, payload);
      setProducts((prev) => prev.map((p) => (p.id === id ? updated : p)));
      return updated;
    } catch (err) {
      return handleError(err, 'Erreur lors de la mise à jour du produit.');
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const publishProduct = useCallback(async (id: string, status: Product['status']): Promise<Product | null> => {
    return updateProduct(id, { status });
  }, [updateProduct]);

  const deleteProduct = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/products/${id}`);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      return true;
    } catch (err) {
      return handleError(err, 'Impossible de supprimer le produit.');
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const addMedia = useCallback(async (productId: string, media: Omit<ProductMedia, 'id' | 'order'>): Promise<ProductMedia | null> => {
    setError(null);
    try {
      const data = await api.post<ProductMedia>(`/products/${productId}/media`, media);
      setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, media: [...p.media, data] } : p)));
      return data;
    } catch (err) {
      return handleError(err, "Impossible d'ajouter le média.");
    }
  }, [handleError]);

  const removeMedia = useCallback(async (productId: string, mediaId: string): Promise<boolean> => {
    setError(null);
    try {
      await api.delete(`/products/${productId}/media/${mediaId}`);
      setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, media: p.media.filter((m) => m.id !== mediaId) } : p)));
      return true;
    } catch (err) {
      return handleError(err, "Impossible de supprimer le média.");
    }
  }, [handleError]);

  const reorderMedia = useCallback(async (productId: string, media: ProductMedia[]): Promise<boolean> => {
    setError(null);
    try {
      await Promise.allSettled(
        media.map((m, idx) =>
          api.patch(`/products/${productId}/media/${m.id}`, { order: idx }).catch(() => null),
        ),
      );
      setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, media } : p)));
      return true;
    } catch (err) {
      return handleError(err, "Impossible de réorganiser les médias.");
    }
  }, [handleError]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const value: ProduitContextValue = {
    products,
    loading,
    error,
    fetchProducts,
    fetchProduct,
    createProduct,
    updateProduct,
    publishProduct,
    deleteProduct,
    addMedia,
    removeMedia,
    reorderMedia,
    clearError,
  };

  return <ProduitContext.Provider value={value}>{children}</ProduitContext.Provider>;
}
